import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { getSettings } from '@/lib/api';

const Footer = () => {
  const [footerSettings, setFooterSettings] = useState(null);
  const [contactSettings, setContactSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [footerRes, contactRes] = await Promise.all([
          getSettings('footer'),
          getSettings('contact')
        ]);
        setFooterSettings(footerRes.data?.value || {});
        setContactSettings(contactRes.data?.value || {});
      } catch (error) {
        console.error('Error fetching footer settings:', error);
      }
    };
    fetchSettings();
  }, []);

  // Fix old brand name if present in database
  const fixBrandName = (text) => {
    if (!text) return text;
    return text.replace(/Eta Global Danışmanlık/g, 'Eta Global Group')
               .replace(/DANIŞMANLIK/g, 'GROUP');
  };

  const footerRaw = footerSettings || {
    description: 'Profesyonel insan kaynakları çözümleri ile işletmelerin sürdürülebilir büyümesine katkı sağlıyoruz.',
    copyright: `© ${new Date().getFullYear()} Eta Global Group. Tüm hakları saklıdır.`,
    companyName: 'ETA GLOBAL',
    companySubtitle: 'GROUP'
  };
  
  const footer = {
    ...footerRaw,
    description: fixBrandName(footerRaw.description),
    copyright: fixBrandName(footerRaw.copyright),
    companySubtitle: fixBrandName(footerRaw.companySubtitle)
  };

  const rawContactData = contactSettings || {
    address: 'İstanbul, Türkiye',
    phone: '0216 606 80 68',
    email: 'info@etaglobalgroup.com',
    workingHours: 'Pzt - Cum: 09:00 - 18:00'
  };

  // Force latest contact details even if DB holds legacy values
  const contact = {
    ...rawContactData,
    phone: '0216 606 80 68',
    email: 'info@etaglobalgroup.com'
  };

  return (
    <footer className="bg-slate-900 text-white">
      {/* Main Footer */}
      <div className="eta-container py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-6">
            <Link to="/" className="inline-block">
              <img 
                src="https://customer-assets-4nw71qhi.emergentagent.net/job_personnel-staffing/artifacts/0igo1bgl_1000283641.png"
                alt="Eta Global Group"
                className="h-56 w-auto object-contain "
              />
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed">
              {footer.description}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Hızlı Bağlantılar
            </h4>
            <ul className="space-y-3">
              <li>
                <Link 
                  to="/" 
                  data-testid="footer-home-link"
                  className="text-slate-400 hover:text-white transition-colors text-sm"
                >
                  Ana Sayfa
                </Link>
              </li>
              <li>
                <Link 
                  to="/hizmetler" 
                  data-testid="footer-services-link"
                  className="text-slate-400 hover:text-white transition-colors text-sm"
                >
                  Hizmetlerimiz
                </Link>
              </li>
              <li>
                <Link 
                  to="/hakkimizda" 
                  data-testid="footer-about-link"
                  className="text-slate-400 hover:text-white transition-colors text-sm"
                >
                  Hakkımızda
                </Link>
              </li>
              <li>
                <Link 
                  to="/iletisim" 
                  data-testid="footer-contact-link"
                  className="text-slate-400 hover:text-white transition-colors text-sm"
                >
                  İletişim
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Hizmetlerimiz
            </h4>
            <ul className="space-y-3">
              <li className="text-slate-400 text-sm">İnsan Kaynakları</li>
              <li className="text-slate-400 text-sm">Dijital Baskı</li>
              <li className="text-slate-400 text-sm">Araç Kiralama</li>
              <li className="text-slate-400 text-sm">Eğitim ve Danışmanlık</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              İletişim
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-slate-400 text-sm">
                <MapPin className="w-5 h-5 text-[#3D94F6] flex-shrink-0 mt-0.5" />
                <span>{contact.address}</span>
              </li>
              <li className="flex items-center gap-3 text-slate-400 text-sm">
                <Phone className="w-5 h-5 text-[#3D94F6] flex-shrink-0" />
                <span>{contact.phone}</span>
              </li>
              <li className="flex items-center gap-3 text-slate-400 text-sm">
                <Mail className="w-5 h-5 text-[#3D94F6] flex-shrink-0" />
                <span>{contact.email}</span>
              </li>
              <li className="flex items-center gap-3 text-slate-400 text-sm">
                <Clock className="w-5 h-5 text-[#3D94F6] flex-shrink-0" />
                <span>{contact.workingHours}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="eta-container py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              {footer.copyright}
            </p>
            <div className="flex items-center gap-6">
              <Link 
                to="/admin/login" 
                data-testid="footer-admin-link"
                className="text-slate-500 hover:text-white transition-colors text-sm"
              >
                Admin Panel
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
